import { jsPDF } from "jspdf";
import { buildAtsCvData } from "./atsCvFormEngine.js";
import { embedAtsCvImportPayloadInPdf } from "./atsCvPdfImportPayload.js";
import { preloadContactIconPngs, writePdfContactRow } from "./atsCvContactIcons.js";
import { renderAtsCvSheetsToPdf } from "./atsCvPreviewPdf.js";
import {
    getProjectLinkMeta,
    measurePdfProjectLinkWidth,
    normalizeProjectUrl,
    preloadProjectLinkIcons,
    writePdfProjectLink,
} from "./atsCvProjectLink.js";

/** ~0.5in margins on A4 (matches preview CSS) */
const MARGIN_X = 13;
const MARGIN_TOP = 13;
const PAGE_BOTTOM = 284;
const CONTENT_W = 210 - MARGIN_X * 2;
const RIGHT_COL_W = CONTENT_W * 0.38;
const LEFT_COL_W = CONTENT_W - RIGHT_COL_W - 2;
const RIGHT_X = MARGIN_X + CONTENT_W;

const FONT = "times";

function ensureSpace(pdf, y, needed) {
    if (y + needed > PAGE_BOTTOM) {
        pdf.addPage();
        return MARGIN_TOP;
    }
    return y;
}

function writeWrapped(pdf, text, x, y, maxW, lineH, options = {}) {
    if (!text) return y;
    const lines = pdf.splitTextToSize(text, maxW);
    lines.forEach((line) => {
        y = ensureSpace(pdf, y, lineH);
        pdf.text(line, x, y, options);
        y += lineH;
    });
    return y;
}

/** Centered lines — x must be page center when using align center */
function writeWrappedCenter(pdf, text, y, maxW, lineH) {
    if (!text) return y;
    const centerX = pdf.internal.pageSize.getWidth() / 2;
    const lines = pdf.splitTextToSize(text, maxW);
    lines.forEach((line) => {
        y = ensureSpace(pdf, y, lineH);
        pdf.text(line, centerX, y, { align: "center" });
        y += lineH;
    });
    return y;
}

function writeSectionTitle(pdf, title, y) {
    y = ensureSpace(pdf, y, 12);
    pdf.setFont(FONT, "bold");
    pdf.setFontSize(11);
    pdf.setTextColor(0, 0, 0);
    pdf.text(title.toUpperCase(), MARGIN_X, y);
    y += 3.5;
    pdf.setDrawColor(0);
    pdf.setLineWidth(0.25);
    pdf.line(MARGIN_X, y, MARGIN_X + CONTENT_W, y);
    return y + 5;
}

/** Keep section heading with following content (avoid orphan title at page bottom). */
function ensureSectionFits(pdf, y, minMm) {
    return ensureSpace(pdf, y, minMm);
}

function estimateTwoColumnSectionMm(itemCount) {
    if (!itemCount) return 0;
    const rows = Math.ceil(itemCount / 2);
    return 12 + rows * 5.5;
}

function writeSectionTitleAndTwoColumns(pdf, title, items, y) {
    y = ensureSectionFits(pdf, y, estimateTwoColumnSectionMm(items.length));
    y = writeSectionTitle(pdf, title, y);
    return writeTwoColumnList(pdf, y, items);
}

function writeSplitRow(pdf, y, leftTitle, leftSub, rightTitle, rightSub) {
    y = ensureSpace(pdf, y, 14);
    const rowStart = y;

    pdf.setFont(FONT, "bold");
    pdf.setFontSize(11);
    if (leftTitle) {
        y = writeWrapped(pdf, leftTitle, MARGIN_X, y, LEFT_COL_W, 4.6);
    }

    if (leftSub) {
        pdf.setFont(FONT, "italic");
        pdf.setFontSize(10);
        y = writeWrapped(pdf, leftSub, MARGIN_X, y, LEFT_COL_W, 4.2);
    }

    let ry = rowStart;
    pdf.setFont(FONT, "bold");
    pdf.setFontSize(11);
    if (rightTitle) {
        const lines = pdf.splitTextToSize(rightTitle, RIGHT_COL_W);
        lines.forEach((line) => {
            ry = ensureSpace(pdf, ry, 4.6);
            pdf.text(line, RIGHT_X, ry, { align: "right" });
            ry += 4.6;
        });
    }
    if (rightSub) {
        pdf.setFont(FONT, "italic");
        pdf.setFontSize(10);
        const lines = pdf.splitTextToSize(rightSub, RIGHT_COL_W);
        lines.forEach((line) => {
            ry = ensureSpace(pdf, ry, 4.2);
            pdf.text(line, RIGHT_X, ry, { align: "right" });
            ry += 4.2;
        });
    }

    return Math.max(y, ry) + 1.5;
}

/** Project title + icon/text link (black, clickable). */
function writeProjectSplitRow(pdf, y, name, technologies, date, link, linkIcons) {
    y = ensureSpace(pdf, y, 14);
    const rowStart = y;
    const url = normalizeProjectUrl(link);
    const { label: linkLabel } = getProjectLinkMeta(link);

    pdf.setTextColor(0, 0, 0);
    pdf.setFont(FONT, "bold");
    pdf.setFontSize(11);

    if (name) {
        y = ensureSpace(pdf, y, 4.6);
        const titleY = y;

        if (url && linkLabel) {
            const gapMm = 1.5;
            const linkSlotW = measurePdfProjectLinkWidth(pdf, link, linkIcons);

            pdf.setFont(FONT, "bold");
            pdf.setFontSize(11);
            const maxNameW = Math.max(20, LEFT_COL_W - linkSlotW - gapMm);
            const nameLines = pdf.splitTextToSize(name, maxNameW);

            pdf.text(nameLines[0], MARGIN_X, titleY);
            const linkX = MARGIN_X + pdf.getTextWidth(nameLines[0]) + gapMm;

            writePdfProjectLink(pdf, linkX, titleY, link, url, linkIcons);

            pdf.setFont(FONT, "bold");
            pdf.setFontSize(11);
            for (let i = 1; i < nameLines.length; i++) {
                y += 4.6;
                y = ensureSpace(pdf, y, 4.6);
                pdf.text(nameLines[i], MARGIN_X, y);
            }
            y += 4.6;
        } else {
            y = writeWrapped(pdf, name, MARGIN_X, y, LEFT_COL_W, 4.6);
        }
    }

    if (technologies) {
        pdf.setFont(FONT, "italic");
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        y = writeWrapped(pdf, technologies, MARGIN_X, y, LEFT_COL_W, 4.2);
    }

    let ry = rowStart;
    pdf.setFont(FONT, "bold");
    pdf.setFontSize(11);
    pdf.setTextColor(0, 0, 0);
    if (date) {
        const lines = pdf.splitTextToSize(date, RIGHT_COL_W);
        lines.forEach((line) => {
            ry = ensureSpace(pdf, ry, 4.6);
            pdf.text(line, RIGHT_X, ry, { align: "right" });
            ry += 4.6;
        });
    }

    return Math.max(y, ry) + 1.5;
}

function writeBullets(pdf, y, bullets) {
    if (!bullets?.length) return y;
    pdf.setFont(FONT, "normal");
    pdf.setFontSize(11);
    const indent = MARGIN_X + 3;
    const bulletW = CONTENT_W - 5;

    for (const bullet of bullets) {
        y = ensureSpace(pdf, y, 6);
        y = writeWrapped(pdf, `• ${bullet}`, indent, y, bulletW, 4.4);
    }
    return y + 1;
}

function writeTwoColumnList(pdf, y, items) {
    if (!items?.length) return y;
    y = ensureSpace(pdf, y, 8);

    if (items.length === 1) {
        return writeBullets(pdf, y, items);
    }

    const mid = Math.ceil(items.length / 2);
    const leftItems = items.slice(0, mid);
    const rightItems = items.slice(mid);
    const colW = (CONTENT_W - 6) / 2;
    const col2X = MARGIN_X + colW + 6;

    let yLeft = y;
    let yRight = y;

    pdf.setFont(FONT, "normal");
    pdf.setFontSize(11);

    for (const item of leftItems) {
        yLeft = ensureSpace(pdf, yLeft, 6);
        yLeft = writeWrapped(pdf, `• ${item}`, MARGIN_X, yLeft, colW, 4.4);
    }
    for (const item of rightItems) {
        yRight = ensureSpace(pdf, yRight, 6);
        yRight = writeWrapped(pdf, `• ${item}`, col2X, yRight, colW, 4.4);
    }

    return Math.max(yLeft, yRight) + 2;
}

/** Width of the portrait in the PDF header, in mm. */
const PHOTO_SIZE = 24;
/** Tallest the box may get, so a portrait shape cannot push the first section
 *  down the page. */
const PHOTO_MAX_HEIGHT = 34;

function writeHeader(pdf, d, contactIcons) {
    const pageW = pdf.internal.pageSize.getWidth();
    let y = MARGIN_TOP;

    // A portrait turns the header from a centred stack into a row: the picture
    // sits at the left margin and everything else ranges left beside it, the
    // same rearrangement the on-screen preview makes.
    const photo = d.photo || "";
    // The box is the shape the crop dialog showed. Only its height varies:
    // the width is what holds the text column in place.
    const photoAspect = Number(d.photoAspect) > 0 ? Number(d.photoAspect) : 1;
    const photoH = Math.min(PHOTO_MAX_HEIGHT, PHOTO_SIZE / photoAspect);
    const photoW = photoH * photoAspect;
    const textLeft = photo ? MARGIN_X + PHOTO_SIZE + 6 : null;
    const centre = photo ? null : pageW / 2;
    const align = photo ? "left" : "center";
    const at = (fallbackCentre) => (photo ? textLeft : fallbackCentre);

    if (photo) {
        try {
            pdf.addImage(photo, MARGIN_X, y - 3, photoW, photoH, undefined, "FAST");
        } catch {
            // An unreadable picture must not cost anyone their CV: the header
            // simply falls back to the centred, photo-less form below.
        }
    }

    if (d.fullName) {
        pdf.setFont(FONT, "bold");
        pdf.setFontSize(20);
        pdf.text(d.fullName, at(centre), y + (photo ? 3 : 0), { align });
        y += 9;
    }

    if (d.jobTitle) {
        pdf.setFont(FONT, "italic");
        pdf.setFontSize(12);
        pdf.text(d.jobTitle, at(centre), y + (photo ? 3 : 0), { align });
        y += 7;
    }

    const row1 = [
        d.location ? { type: "location", text: d.location } : null,
        d.email ? { type: "email", text: d.email } : null,
        d.phone ? { type: "phone", text: d.phone } : null,
    ].filter(Boolean);

    const row2 = [
        d.linkedin ? { type: "linkedin", text: d.linkedin } : null,
        d.website ? { type: "website", text: d.website } : null,
    ].filter(Boolean);

    if (photo) y += 3;

    if (contactIcons && (row1.length || row2.length)) {
        if (row1.length) {
            y = writePdfContactRow(pdf, y, row1, contactIcons, FONT);
        }
        if (row2.length) {
            y = writePdfContactRow(pdf, y, row2, contactIcons, FONT);
        }
    } else {
        pdf.setFont(FONT, "normal");
        pdf.setFontSize(10);
        const contactLine1 = [d.location, d.email, d.phone].filter(Boolean).join("  |  ");
        const contactLine2 = [d.linkedin, d.website].filter(Boolean).join("  |  ");
        if (contactLine1) {
            y = writeWrappedCenter(pdf, contactLine1, y, CONTENT_W, 4.2);
        }
        if (contactLine2) {
            y = writeWrappedCenter(pdf, contactLine2, y, CONTENT_W, 4.2);
        }
    }

    // Whatever the text came to, the first section still has to start below the
    // portrait — a short header beside a tall picture would otherwise overlap it.
    if (photo) y = Math.max(y, MARGIN_TOP - 3 + photoH + 2);

    return y + 6;
}

/**
 * Text PDF with proper page breaks — matches preview layout, selectable text, re-importable.
 * @param {import("./atsCvFormEngine.js").INITIAL_ATS_CV_FORM} form
 */
export async function generateAtsCvPdfBlobFromForm(form) {
    const d = buildAtsCvData(form);
    const t = d.sectionTitles;
    const hasContact =
        d.location || d.email || d.phone || d.linkedin || d.website;
    const contactIcons = hasContact ? await preloadContactIconPngs() : null;
    const projectLinkIcons =
        d.projects.some((p) => p.link) ? await preloadProjectLinkIcons() : null;
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

    pdf.setProperties({
        title: d.fullName ? `${d.fullName} - CV` : "CV",
        subject: "ATS CV",
        author: d.fullName || "Sohozkaj",
        creator: "Sohozkaj ATS CV Maker",
    });

    let y = writeHeader(pdf, d, contactIcons);

    if (d.summary) {
        const summaryLines = pdf.splitTextToSize(d.summary, CONTENT_W);
        y = ensureSectionFits(pdf, y, 12 + summaryLines.length * 4.6);
        y = writeSectionTitle(pdf, t.summary, y);
        pdf.setFont(FONT, "normal");
        pdf.setFontSize(11);
        y = writeWrapped(pdf, d.summary, MARGIN_X, y, CONTENT_W, 4.6, { align: "justify" });
        y += 3;
    }

    if (d.experiences.length) {
        y = writeSectionTitle(pdf, t.experience, y);
        for (const exp of d.experiences) {
            y = ensureSpace(pdf, y, 20);
            y = writeSplitRow(
                pdf,
                y,
                exp.jobTitle,
                exp.company,
                exp.dateRange,
                exp.location
            );
            y = writeBullets(pdf, y, exp.bullets);
            y += 2;
        }
    }

    if (d.education.length) {
        y = writeSectionTitle(pdf, t.education, y);
        for (const edu of d.education) {
            y = ensureSpace(pdf, y, 18);
            y = writeSplitRow(
                pdf,
                y,
                edu.degree,
                edu.institution,
                edu.graduationYear,
                edu.location
            );
            const bullets = edu.bullets?.length ? edu.bullets : [];
            y = writeBullets(pdf, y, bullets);
            if (!bullets.length && edu.details) {
                pdf.setFont(FONT, "normal");
                pdf.setFontSize(11);
                y = writeWrapped(pdf, edu.details, MARGIN_X, y, CONTENT_W, 4.4);
            }
            y += 2;
        }
    }

    if (d.projects.length) {
        y = writeSectionTitle(pdf, t.projects, y);
        for (const proj of d.projects) {
            y = ensureSpace(pdf, y, 20);
            y = writeProjectSplitRow(
                pdf,
                y,
                proj.name,
                proj.technologies || undefined,
                proj.date,
                proj.link,
                projectLinkIcons
            );
            y = writeBullets(pdf, y, proj.bullets);
            y += 2;
        }
    }

    if (d.skills.length) {
        y = writeSectionTitleAndTwoColumns(pdf, t.skills, d.skills, y);
    }

    if (d.certifications.length) {
        y = writeSectionTitleAndTwoColumns(pdf, t.certifications, d.certifications, y);
    }

    if (d.languages.length) {
        y = writeSectionTitleAndTwoColumns(pdf, t.languages, d.languages, y);
    }

    for (const sec of d.customSections) {
        if (sec.layout === "twoColumn" && sec.items?.length) {
            y = writeSectionTitleAndTwoColumns(pdf, sec.title, sec.items, y);
        } else {
            const est =
                sec.layout === "paragraph" && sec.paragraph
                    ? 12 +
                      pdf.splitTextToSize(sec.paragraph, CONTENT_W).length * 4.6
                    : 12 + (sec.items?.length || 0) * 5;
            y = ensureSectionFits(pdf, y, est);
            y = writeSectionTitle(pdf, sec.title, y);
            if (sec.layout === "paragraph" && sec.paragraph) {
                pdf.setFont(FONT, "normal");
                pdf.setFontSize(11);
                y = writeWrapped(pdf, sec.paragraph, MARGIN_X, y, CONTENT_W, 4.6, {
                    align: "justify",
                });
            } else {
                y = writeBullets(pdf, y, sec.items);
            }
            y += 3;
        }
    }

    embedAtsCvImportPayloadInPdf(pdf, form);

    return pdf.output("blob");
}


/**
 * The PDF of what is on screen.
 *
 * The preview's own pages are redrawn into the file as text (see
 * `atsCvPreviewPdf.js`), so the export lands on top of the preview and an
 * applicant tracking system can still read every word of it. With no preview to
 * read — an export asked for before the pages exist — the form is typeset from
 * scratch instead.
 *
 * @param {HTMLElement | null | undefined} previewRoot `.ats-cv-pages-root`
 * @param {import("./atsCvFormEngine.js").INITIAL_ATS_CV_FORM} form
 */
export async function generateAtsCvPdfBlobFromPreview(previewRoot, form) {
    const stack = previewRoot?.querySelector(".ats-cv-pages-stack");
    const pages = stack
        ? Array.from(stack.querySelectorAll(".ats-cv-page-sheet"))
        : [];

    if (!pages.length) {
        return generateAtsCvPdfBlobFromForm(form);
    }

    const pdf = await renderAtsCvSheetsToPdf(pages);
    embedAtsCvImportPayloadInPdf(pdf, form);
    return pdf.output("blob");
}

export async function downloadAtsCvPdf(previewElement, form, filename = "ats-cv.pdf") {
    const blob = await generateAtsCvPdfBlobFromPreview(previewElement, form);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

/** @deprecated Use print modal flow from ATS CV page */
export async function printAtsCv(previewElement, form) {
    const blob = await generateAtsCvPdfBlobFromPreview(previewElement, form);
    const url = URL.createObjectURL(blob);
    return { url, revoke: () => URL.revokeObjectURL(url) };
}
